// frontend/src/features/auth/authSlice.js
// This Redux slice manages authentication state—handling login, logout, and fetching the current user, storing tokens, updating Axios headers, and exposing selectors for user info, roles, and agency ID.
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

// Thunk to log in
export const login = createAsyncThunk(
  'auth/login',
  async ({ email, password }, { rejectWithValue }) => {
    try {
      // 1️⃣ Authenticate and get token
      const resp1 = await axios.post('/auth/login', { email, password });
      const { accessToken } = resp1.data;

      // 2️⃣ Persist token & set header
      localStorage.setItem('accessToken', accessToken);
      axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;

      // 3️⃣ Fetch full user (includes agency_id for agency roles)
      const resp2 = await axios.get('/auth/me');
      // --- DEBUG ---
      console.log('[auth/login] token length:', accessToken?.length);
      console.log('[auth/login] axios Authorization:', axios.defaults.headers.common.Authorization);
      console.log('[auth/login] user:', resp2.data.user);
      // ------------
      return resp2.data.user;
    } catch (err) {
      console.error('[auth/login] error:', err?.response?.data || err);
      return rejectWithValue(err.response?.data?.error || 'Login failed');
    }
  }
);

// Thunk to fetch current user on app start
export const fetchCurrentUser = createAsyncThunk(
  'auth/fetchCurrentUser',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get('/auth/me');
      // --- DEBUG ---
      console.log('[auth/fetchCurrentUser] user:', response.data.user);
      // ------------
      return response.data.user;
    } catch (err) {
      console.error('[auth/fetchCurrentUser] error:', err?.response?.data || err);
      return rejectWithValue(err.response?.data?.error || 'Fetch failed');
    }
  }
);

// Thunk to log out
export const logout = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      await axios.post('/auth/logout');
      return;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Logout failed');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    userInfo: null,
    loading: false,
    error: null
  },
  reducers: {},
  extraReducers: builder => {
    builder
      // Login
      .addCase(login.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.userInfo = action.payload;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch current user
      .addCase(fetchCurrentUser.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        state.loading = false;
        state.userInfo = action.payload;
      })
      .addCase(fetchCurrentUser.rejected, (state, action) => {
        state.loading = false;
        state.userInfo = null;
      })
      // Logout
      .addCase(logout.fulfilled, state => {
        state.userInfo = null;
        state.loading = false;
        state.error = null;
        localStorage.removeItem('accessToken');
        delete axios.defaults.headers.common.Authorization;
      });
  }
});

// Selectors
export const selectCurrentUser = state => state.auth.userInfo;
export const selectUserRole = state => state.auth.userInfo?.role;
export const selectIsUpsAdmin = state => state.auth.userInfo?.role === 'ups_admin';
export const selectAgencyIdForUser = state => state.auth.userInfo?.agency_id;

export default authSlice.reducer;
